import React from 'react';
import { Trophy, Target, ChartLineUp } from '@phosphor-icons/react';

/**
 * Reusable Convergence panel — shows ELITE/STRONG/MODERATE tiers
 * built from F1 top-5 and F2 top-5.
 *
 * Props:
 *   convergence: { elite, strong, moderate, f1_top5, f2_top5, last_range/last_pattern, next_dow }
 *   itemLabel: 'Range' | 'Pattern'
 *   renderItem: (item) => ReactNode  (custom render for ranges vs sign patterns)
 *   f1Lookup: (item) => { score, score_v_adj }
 *   f2Lookup: (item) => { score, components }
 */
export default function ConvergencePanel({ convergence, itemLabel = 'Item', renderItem, f1Lookup, f2Lookup }) {
  if (!convergence) return null;
  const { elite = [], strong = [], moderate = [], f1_top5 = [], f2_top5 = [] } = convergence;
  const lastObs = convergence.last_range || convergence.last_pattern || '';
  const dowMap = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const nextDowName = convergence.next_dow != null ? dowMap[convergence.next_dow] : '?';

  const renderItemFn = renderItem || ((it) => <span className="font-mono font-bold">{it}</span>);

  return (
    <div className="space-y-5" data-testid="convergence-panel">
      {/* === Verdict Card on top === */}
      <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 rounded-xl p-6 text-white shadow-2xl ring-4 ring-emerald-300 ring-offset-2" data-testid="convergence-verdict-card">
        <div className="flex items-center gap-3 mb-3">
          <Trophy size={32} weight="fill" className="text-amber-300" />
          <div>
            <h2 className="text-2xl font-black tracking-tight">Cross-Confirmed Verdict</h2>
            <p className="text-[12px] text-emerald-100">High-confidence picks where BOTH formulas independently agree.</p>
          </div>
        </div>

        {elite.length > 0 ? (
          <div className="bg-white/15 backdrop-blur-sm rounded-lg p-4 border border-white/30">
            <div className="text-[11px] uppercase font-black tracking-wider text-amber-200 mb-2">🎯 Elite Convergence (Top-3 in BOTH)</div>
            <div className="flex flex-wrap gap-2">
              {elite.map((it, i) => (
                <div key={i} className="bg-amber-300 text-amber-950 rounded-lg px-4 py-2 font-mono font-black text-lg shadow-lg" data-testid={`elite-item-${i}`}>
                  {renderItemFn(it)}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white/10 rounded-lg p-3 text-sm text-emerald-100 italic">
            No elite convergence right now — formulas disagree on top-3. Check STRONG and MODERATE tiers below.
          </div>
        )}

        <div className="mt-3 text-[11px] text-emerald-100 font-mono">
          Last {itemLabel.toLowerCase()}: <span className="font-bold text-white">{lastObs || '—'}</span>
          {' · '}
          Next draw DoW: <span className="font-bold text-white">{nextDowName}</span>
        </div>
      </div>

      {/* === Tier breakdown === */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* ELITE tier */}
        <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 shadow-md" data-testid="tier-elite">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={20} weight="fill" className="text-amber-600" />
            <span className="text-sm font-black text-amber-900">🥇 ELITE</span>
            <span className="ml-auto bg-amber-200 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full">Top-3 in BOTH</span>
          </div>
          {elite.length === 0 ? (
            <p className="text-xs text-amber-700 italic">No items in this tier.</p>
          ) : (
            <div className="space-y-2">
              {elite.map((it, i) => (
                <div key={i} className="bg-white border border-amber-300 rounded p-2">
                  <div className="text-base">{renderItemFn(it)}</div>
                  {f1Lookup && f2Lookup && (
                    <div className="text-[10px] text-gray-500 font-mono mt-1">
                      F1: {f1Lookup(it)?.score_v_adj || '—'}  |  F2: {f2Lookup(it)?.score || '—'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* STRONG tier */}
        <div className="bg-blue-50 border-2 border-blue-400 rounded-xl p-4 shadow-md" data-testid="tier-strong">
          <div className="flex items-center gap-2 mb-2">
            <Target size={20} weight="fill" className="text-blue-600" />
            <span className="text-sm font-black text-blue-900">🥈 STRONG</span>
            <span className="ml-auto bg-blue-200 text-blue-900 text-[10px] font-bold px-2 py-0.5 rounded-full">Top-3 ∩ Top-5</span>
          </div>
          {strong.length === 0 ? (
            <p className="text-xs text-blue-700 italic">No items in this tier.</p>
          ) : (
            <div className="space-y-2">
              {strong.map((it, i) => (
                <div key={i} className="bg-white border border-blue-300 rounded p-2">
                  <div className="text-base">{renderItemFn(it)}</div>
                  {f1Lookup && f2Lookup && (
                    <div className="text-[10px] text-gray-500 font-mono mt-1">
                      F1: {f1Lookup(it)?.score_v_adj || '—'}  |  F2: {f2Lookup(it)?.score || '—'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* MODERATE tier */}
        <div className="bg-gray-50 border-2 border-gray-400 rounded-xl p-4 shadow-md" data-testid="tier-moderate">
          <div className="flex items-center gap-2 mb-2">
            <ChartLineUp size={20} weight="fill" className="text-gray-600" />
            <span className="text-sm font-black text-gray-900">🥉 MODERATE</span>
            <span className="ml-auto bg-gray-200 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full">Top-5 (one)</span>
          </div>
          {moderate.length === 0 ? (
            <p className="text-xs text-gray-700 italic">No items in this tier.</p>
          ) : (
            <div className="space-y-2">
              {moderate.map((it, i) => (
                <div key={i} className="bg-white border border-gray-300 rounded p-2">
                  <div className="text-base">{renderItemFn(it)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* === Side-by-side top-5 === */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border-2 border-blue-300 rounded-xl p-4 shadow-sm" data-testid="f1-top5-list">
          <h3 className="text-sm font-black text-blue-900 mb-3 flex items-center gap-2">
            <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs">F1</span>
            Top-5 by Long-Term Score
          </h3>
          <ol className="space-y-1.5">
            {f1_top5.map((it, i) => (
              <li key={i} className={`flex items-center gap-2 px-3 py-2 rounded ${i < 3 ? 'bg-blue-50 border-l-4 border-blue-500 font-bold' : 'bg-gray-50'}`}>
                <span className="text-[11px] font-mono text-gray-500 w-4">#{i + 1}</span>
                <div className="flex-1">{renderItemFn(it)}</div>
                {f1Lookup && f1Lookup(it) && (
                  <span className="font-mono text-xs text-blue-700 font-bold">{f1Lookup(it)?.score_v_adj}</span>
                )}
              </li>
            ))}
          </ol>
        </div>

        <div className="bg-white border-2 border-orange-300 rounded-xl p-4 shadow-sm" data-testid="f2-top5-list">
          <h3 className="text-sm font-black text-orange-900 mb-3 flex items-center gap-2">
            <span className="bg-orange-600 text-white px-2 py-0.5 rounded text-xs">F2</span>
            Top-5 by Next-Draw Score
          </h3>
          <ol className="space-y-1.5">
            {f2_top5.map((it, i) => (
              <li key={i} className={`flex items-center gap-2 px-3 py-2 rounded ${i < 3 ? 'bg-orange-50 border-l-4 border-orange-500 font-bold' : 'bg-gray-50'}`}>
                <span className="text-[11px] font-mono text-gray-500 w-4">#{i + 1}</span>
                <div className="flex-1">{renderItemFn(it)}</div>
                {f2Lookup && f2Lookup(it) && (
                  <span className="font-mono text-xs text-orange-700 font-bold">{f2Lookup(it)?.score}</span>
                )}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
